/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2018, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./DataItemAPISpec',
	'../DataItemAPI',
	'../../widgets/livewidget/nls/StringResources',
	'./Union'
], 	(_, APIFactory, DataItemAPISpec, DataItemAPI, StringResources, Union) => {
	const UP = 'up';
	const DOWN = 'down';

	class DataItem extends DataItemAPISpec{
		constructor(dataItemModel, dataSourceAPI, transaction, locale) {
			super();
			this.locale = locale;
			this.dataItemModel = dataItemModel;
			this.dataSourceAPI = dataSourceAPI;
			this.transaction = transaction;
			this._api = APIFactory.createAPI(this, [DataItemAPI]);
		}

		isRootDataItem () {
			return !(this instanceof UnionDataItem);
		}

		destroy() {
			this.locale = null;
			this.dataItemModel = null;
			this.dataSourceAPI = null;
			this.transaction = null;
			this._api = null;
			if (this.unionImpl){
				this.unionImpl.destroy();
			}
		}

		getAPI() {
			return this._api;
		}

		getId() {
			return this.dataItemModel.id;
		}

		setLabel(label) {
			this.dataItemModel.itemLabel = label;
		}

		getLabel() {
			const binning = this.getBinning();
			let  label = binning && binning.label;
			if (!label){
				const metadata = this.getMetadataColumn();
				const metadataColumnLabel = metadata && metadata.getLabel();
				if (metadataColumnLabel) {
					//We've got good metadata information so return it
					//AND save the latest metadataColumnLabel to the widgetSpec model for this dataItem.
					this.dataItemModel.itemLabel = metadataColumnLabel;
					label = metadataColumnLabel;
				} else {
					//No Metadata available (eg missing column)....return the last "good" itemLabel.
					label = this.dataItemModel.itemLabel;
				}
			}
			return label;
		}

		getColumnId() {
			return this.dataItemModel.itemId;
		}

		getNavigationPathId() {
			return this.dataItemModel.navigationPathId;
		}

		setNavigationPathId(id) {
			this.dataItemModel.navigationPathId = id;
		}

		getDataType() {
			const mdColumn = this.getMetadataColumn();
			return mdColumn  ? mdColumn.getDataType() : null;
		}

		isDateOrTimeType() {
			let dataType = this.getDataType();
			return (dataType === 'date' || dataType === 'datetime' || dataType === 'time' || dataType === 'year') ? true : false;
		}

		getType() {
			const mdColumn = this.getMetadataColumn();
			return mdColumn  ? mdColumn.getType() : null;
		}

		getAggregation() {
			let aggregation = this.dataItemModel.aggregate;
			if (!aggregation){
				const mdColumn = this.getMetadataColumn();
				aggregation = mdColumn ? mdColumn.getDefaultAggregation() : null;
			}
			return aggregation;
		}


		setAggregation (aggregation) {
			this.dataItemModel.aggregate = aggregation;
		}

		hasDefaultAggregation() {
			return !this.dataItemModel.aggregate;
		}

		getMetadataColumn() {
			return this.dataSourceAPI ? this.dataSourceAPI.getMetadataColumn(this.getColumnId()) : null;
		}

		hasDefaultFormat() {
			return !this.dataItemModel.format || !this.dataItemModel.format.formatSpec || _.isEmpty(this.dataItemModel.format.formatSpec);
		}

		getFormat() {
			let format = this.dataItemModel.format && this.dataItemModel.format.formatSpec;
			// Should not be in the spec.. should weconsider an upgrade to clean it ?
			if (format){
				delete format.local;
			}

			if (_.isEmpty(format)){
				format = null;
			}

			if (format) {
				format = _.extend({}, format, { locale: this.locale });
				//adding this condition here, to remove abbreviation when Auto-Grouping is turned on
				const binningDefinition = this.dataItemModel.binningDefinition;
				if (binningDefinition) {
					if (format.decimalFormatLength && format.decimalFormatLength === 'short') {
						delete format.decimalFormatLength;
					}
				}
			}

			if (!format) {
				const mdColumn = this.getMetadataColumn();
				format = mdColumn ? mdColumn.getFormat() : null;
			}

			if (format && !format.locale) {
				format.locale = this.locale;
			}

			return format;

		}

		setFormat(formatSpec) {
			this.dataItemModel.format = { formatSpec: formatSpec };
		}

		_getSortSelection(selections){
			return selections.find((selection) =>{
				if (selection.operation !== 'order') {
					return false;
				}
				return !selection.sort ? false : true;
			});
		}

		hasModelSort() {
			throw 'Need to implement hasModelSort for DataItem';
		}

		_getUserSort() {
			var selections = this.dataItemModel.selection;
			if (!selections) {
				return null;
			}
			var selection = this._getSortSelection(selections);

			var result = null;
			if (selection) {
				var sortSelection = selection.sort;
				result = {
					type: sortSelection.type,
					priority: sortSelection.priority,
					by: sortSelection.by
				};

				if (sortSelection.rankSort) {
					result.rankSort = true;
				}

				if(sortSelection.custom){
					result.custom = sortSelection.custom;
				}

				var contextArray = selection.context;
				if (contextArray) {
					var context = contextArray.length && contextArray[0];
					if (context) {
						result.context = context.itemId;
					}
				}
			}

			return result;
		}

		hasDefaultSort() {
			return !this._getUserSort();
		}

		getSort() {
			var sort = this._getUserSort();
			if (sort) {
				return sort;
			}
		}


		_getTopBottomSelection(selections){
			return !selections ? null : selections.find((selection) => {
				return !!selection.topBottom;
			});
		}

		getTopBottom() {
			var selections = this.dataItemModel.selection;
			if (!selections) {
				return null;
			}

			var selection = this._getTopBottomSelection(selections);

			if (!selection) {
				return null;
			}
			var topBottom = selection.topBottom;
			if (!topBottom) {
				return null;
			}

			var result = {
				type: topBottom.type,
				value: topBottom.value
			};

			// use != to check for both null and undefined
			if (topBottom.rank != null){
				result.rank = topBottom.rank;
			}

			var contextArray = selection.context;
			if (contextArray) {
				var context = contextArray.length && contextArray[0];
				if (context) {
					result.context = {
						itemId:  context.itemId
					};
					if (context.aggregate){
						result.context.aggregate = context.aggregate;
					}
				}
			}

			return result;
		}

		_removeSelection(getter) {
			var selections = this.dataItemModel.selection;
			if (selections) {
				var selection = getter(selections);
				if (selection) {
					const index = selections.indexOf(selection);
					if (index > -1) {
						selections.splice(index, 1);
					}
				}
			} //else nothing to do
		}

		_initSelections(getter) {
			var selections = this.dataItemModel.selection;
			if (!selections) {
				selections = [];
				this.dataItemModel.selection = selections;
			}
			this._removeSelection(getter);
			return selections;
		}

		setTopBottom(topBottomInfo, transactionToken) {
			// TODO livewidget_cleanup -- test and fix the undo redo here
			if(this.getUnion().getDataItemList().length){
				this.getUnion().setDataItems([], transactionToken);
			}
			if (!topBottomInfo){
				this._removeSelection(this._getTopBottomSelection);
			} else {
				var selections = this._initSelections(this._getTopBottomSelection);

				var action = {
					operation: 'keep',
					topBottom: {
						type: topBottomInfo.type,
						value: topBottomInfo.value
					}
				};

				if (topBottomInfo.context) {
					action.context = [{
						itemId: topBottomInfo.context.itemId,
						aggregate: topBottomInfo.context.aggregate
					}];
				}

				if (topBottomInfo.rank) {
					action.topBottom.rank = true;
				}
				selections.push(action);
			}

		}

		_isolate(drillSpec, selections) {
			selections.push({
				'operation': 'keep',
				'set': [drillSpec.mun],
				'isolated': true
			});
		}

		_getIsolated () {
			var selections = this.dataItemModel.selection;
			if (!selections) {
				return false;
			}

			return selections.find((selectionModel) => {
				return selectionModel.isolated;
			});
		}

		_getDrillSelection(selections) {
			return !selections ? null : selections.find((selection) => {
				return !!selection.children || !!selection.drillUp || !!selection.isolated;
			});
		}

		_drill(drillSpec) {
			const isolatedValue = this.getIsolatedValue();
			const selections = this._initSelections(this._getDrillSelection);
			if (drillSpec.isLeafMember) {
				this._isolate(drillSpec, selections);
			} else {
				const command = (drillSpec.op === UP) ? 'drillUp' : 'children';

				//The spec dictates that the selection command for drill up is `drillUp`, but drill down is `children`.
				//The [command] sets the variable to be either drillUp or children, thus saving redundant code to create the object.
				const param = {
					operation: 'add'
				};

				//Drill up with isolatedValue should be based on the isolated member because in isolated state the member is constructed
				//as a set with the member only and this member is a leaf member.
				//E.g. {"operation":"keep","set":["[Sales (analysis)].[Time].[Time].[Day]->[Time].[2010].[20101].[201001].[20100112]"]}
				param[command] = !!isolatedValue && drillSpec.op === UP ? isolatedValue : drillSpec.mun;
				selections.push(param);
			}
		}

		canDrillDown() {
			return !this._getIsolated();
		}

		clearDrill(transactionToken) {
			if(this.getUnion().getDataItemList().length){
				this.getUnion().setDataItems([], transactionToken);
			}

			this._removeSelection(this._getDrillSelection);
		}


		drillDown(value, transactionToken, parentValue) {
			const metadata = this.getMetadataColumn();
			return metadata.isLeafMember(value).then(isLeafMemberFlag => {
				//clear union before drilling
				// TODO livewidget_cleanup -- test and fix the undo redo here
				if(this.getUnion().getDataItemList().length){
					this.getUnion().setDataItems([], transactionToken);
				}

				// TODO Pass parentValue as a temporary solution to handle drill on leaf member.
				// Hide it from public API until we find a better solution.
				var drillSpec = {
					op: DOWN,
					mun: value,
					pun: parentValue,
					isLeafMember: isLeafMemberFlag
				};
				this._drill(drillSpec);
			});
		}

		getDrillDownValue() {
			const selection = this._getDrillSelection(this.dataItemModel.selection);
			return selection ? selection.children || (selection.set && selection.set[0]) : null;
		}

		getIsolatedValue() {
			const isolated = this._getIsolated();
			return isolated && isolated.set && isolated.set[0];
		}

		drillUp(itemId, transactionToken) {
			// TODO livewidget_cleanup -- test and fix the undo redo here
			if(this.getUnion().getDataItemList().length){
				this.getUnion().setDataItems([], transactionToken);
			}
			var drillSpec = {
				op: UP,
				mun: itemId
			};
			this._drill(drillSpec);
			return Promise.resolve();
		}

		getDrillUpValue() {
			var selection = this._getDrillSelection(this.dataItemModel.selection);
			return selection ? selection.drillUp : null;
		}

		setBinning(binningSpec) {
			let binning;
			if (binningSpec) {
				binning = {
					id: this.id,
					binning: {
						auto: {
							numberOfBins: binningSpec.bins
						}
					},
					label: binningSpec.label || this.getLabel() + ' ' + StringResources.get('autobinAxisLabel')
				};
			}
			this.dataItemModel.binningDefinition = binning;
		}

		getBinning() {
			let binningDefinition = this.dataItemModel.binningDefinition;
			return binningDefinition ? {
				bins: binningDefinition.binning.auto.numberOfBins,
				label: binningDefinition.label
			}: undefined;
		}


		setSort(sortSpec) {
			var selections = this._initSelections(this._getSortSelection);
			if (sortSpec && (sortSpec.type ||  sortSpec.custom)) {
				// If we have a context, we default to a 'value' sortBy otherwise it is 'caption'
				var defaultSortBy = sortSpec.context ? 'value' : 'caption';
				var sort = {
					operation: 'order',
					sort: {
						type: sortSpec.type,
						by: sortSpec.by || defaultSortBy,
						priority: sortSpec.priority,
						custom: sortSpec.custom
					}
				};

				if (sortSpec.context) {
					sort.context = [{
						itemId: sortSpec.context
					}];
				}

				if (sortSpec.rankSort) {
					sort.sort.rankSort = true;
				}
				selections.push(sort);
			}
		}

		getUnion() {
			if (!this.unionImpl){
				if (!this.dataItemModel.union) {
					this.dataItemModel.union = {};
				}
				this.unionImpl = new Union(this.dataItemModel.union, UnionDataItem, this, this.dataSourceAPI, this.transaction , this.locale);
				APIFactory.setParentChildRelation(this, this.unionImpl);
			}
			return this.unionImpl.getAPI();
		}

		isColumnUnavailable() {
			const md = this.getMetadataColumn();
			return !md || md.isMissing();
		}

	}

	class UnionDataItem extends DataItem {

		drillDown(childUseValue) {
			var drillSpec = {
				op: DOWN,
				mun: childUseValue,
				isLeafMember: false
			};
			return this._drill(drillSpec);
		}
	}


	return DataItem;
});
